import { FlagFilled, FlagOutlined } from "@ant-design/icons";
import { Input, Popover } from "antd";
import { useEffect, useState } from "react";
import api from "../api/axios";
import { useView } from "../contexts/ViewContext";
import styles from "./ContentFlag.module.css";

const NOTE_MAX_LENGTH = 2000;

function ContentFlag({ view }) {
    const { learningJourneyId, readOnly } = useView();
    const [open, setOpen] = useState(false);
    // `sentNote` is the last successfully sent note (server truth; "" until flagged).
    // `note` is the popover's own draft, reset from `sentNote` on every open so a typed
    // but never-sent edit is discarded on close rather than lingering as a phantom draft.
    const [sentNote, setSentNote] = useState("");
    const [note, setNote] = useState("");
    const [flagged, setFlagged] = useState(false);

    // A new activity is a fresh surface: the flag never carries a raised state or a
    // draft note over from whatever the student was previously looking at.
    useEffect(() => {
        setOpen(false);
        setNote("");
        setSentNote("");
        setFlagged(false);
    }, [view.view_id]);

    useEffect(() => {
        if (!open) return;

        const onKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open]);

    if (readOnly) return null;

    const onOpenChange = (nextOpen) => {
        if (nextOpen) setNote(sentNote);
        setOpen(nextOpen);
    };

    // Plain elements, not antd's Button -- this app's global `button, .ant-btn` rule
    // (styles/globals.css) forces every button onto the CTA-sized gradient treatment,
    // which these two slim inline actions must not carry.
    const activateOnKey = (handler) => (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handler();
        }
    };

    const send = async () => {
        try {
            await api.post(`/workspace/content-rating/flag/`, {
                learning_journey_id: learningJourneyId,
                view_id: view.view_id,
                note,
            });
            setSentNote(note);
            setFlagged(true);
            setOpen(false);
        } catch (error) {
            console.error("Failed to send content flag:", error);
        }
    };

    const remove = async () => {
        try {
            await api.post(`/workspace/content-rating/withdraw/`, {
                learning_journey_id: learningJourneyId,
                view_id: view.view_id,
            });
            setFlagged(false);
            setSentNote("");
            setNote("");
            setOpen(false);
        } catch (error) {
            console.error("Failed to withdraw content flag:", error);
        }
    };

    return (
        <Popover
            trigger="click"
            open={open}
            onOpenChange={onOpenChange}
            placement="bottomRight"
            rootClassName={styles.popoverRoot}
            content={
                <div className={styles.content}>
                    <div className={styles.title}>What is off?</div>
                    <Input.TextArea
                        className={styles.note}
                        size="small"
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        maxLength={NOTE_MAX_LENGTH}
                        placeholder="(Optional) Tell us what's wrong"
                        autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                    <div className={styles.actions}>
                        {flagged && (
                            <span
                                role="button"
                                tabIndex={0}
                                className={`${styles.actionButton} ${styles.removeButton}`}
                                onClick={remove}
                                onKeyDown={activateOnKey(remove)}
                            >
                                Remove
                            </span>
                        )}
                        <span
                            role="button"
                            tabIndex={0}
                            className={`${styles.actionButton} ${styles.sendButton}`}
                            onClick={send}
                            onKeyDown={activateOnKey(send)}
                        >
                            Send
                        </span>
                    </div>
                </div>
            }
        >
            <span
                role="button"
                tabIndex={0}
                className={`${styles.trigger} ${flagged ? styles.flagged : ""}`}
                aria-label="Flag this content"
            >
                {flagged ? <FlagFilled /> : <FlagOutlined />}
            </span>
        </Popover>
    );
}

export default ContentFlag;
